import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addProduct,
  clearState,
  fetchAllCategories,
  fetchAllGrades,
  fetchAllSubjects,
  fetchAllSections,
} from "../../redux/Slices/ProductSlice";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getAllTeachers } from "../../redux/Slices/TeachersSlice";
import { fetchVariants, fetchVariantValues } from "../../redux/Slices/VariantSlice";

const AddProduct = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const {i18n} = useTranslation("global");
  const {
    addLoading,
    addError,
    addSuccessMessage,
    categories,
    grades,
    subjects,
    sections,
  } = useSelector((state) => state.products);
  const {
    all_teachers
  } = useSelector((state) => state.teachers);
  const { variants, variantValues } = useSelector((state) => state.variants);
  // console.log('AddProduct - Categories:', categories);
  // console.log('AddProduct - Grades:', grades);
  // console.log('AddProduct - Subjects:', subjects);
  // console.log('AddProduct - Sections:', sections);

  const [formData, setFormData] = useState({
    title_en: "",
    title_ar: "",
    subtitle_en: "",
    subtitle_ar: "",
    status: 1,
    desc_en: "",
    desc_ar: "",
    note_en: "",
    note_ar: "",
    fake_price: 0,
    real_price: 0,
    discount: 0,
    qty: "",
    stock: "",
    sku: "",
    preorder_capacity: "",
    is_preorder: 0,
    category_id: "",
    grade_id: "",
    subject_id: "",
    section_id: "",
    teacher_id: "",
    sort_by: 0,
    is_free_shipping: 0,
    features: [{ text_en: "", text_ar: "" }],
    sliders: [],
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const [errors, setErrors] = useState({});

  // Custom attributes for categories
  const [customAttributes, setCustomAttributes] = useState({});

  // Variants state
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedVariantValues, setSelectedVariantValues] = useState({});
  const [generatedSKUs, setGeneratedSKUs] = useState([]);

  useEffect(() => {
    // تحميل البيانات للـ dropdowns باستخدام Redux
    dispatch(fetchAllCategories());
    dispatch(fetchAllGrades());
    dispatch(fetchAllSubjects());
    dispatch(fetchAllSections());
    dispatch(getAllTeachers());
    dispatch(fetchVariants());
    dispatch(fetchVariantValues());
  }, [dispatch]);

  const selectedCategoryObj = categories?.find(c => String(c.id) === String(formData.category_id));

  const isBookOrCodeCategory = (cat) => {
    if (!cat) return false;
    const nameEn = (cat.name_en || "").toLowerCase().trim();
    return nameEn.includes("books") || nameEn.includes("codes");
  };

  const isEducational = selectedCategoryObj ? isBookOrCodeCategory(selectedCategoryObj) : false;
  const showVariantsUI = selectedCategoryObj ? !isEducational : false;

  // Get dynamic attributes based on category name
  const dynamicAttributes = React.useMemo(() => {
    if (!selectedCategoryObj) return [];
    const nameEn = (selectedCategoryObj.name_en || "").toLowerCase();
    const nameAr = (selectedCategoryObj.name_ar || "");

    if (nameEn.includes("books") || nameAr.includes("كتب")) {
      return [
        { key: "page_count", labelAr: "عدد الصفحات", labelEn: "Page Count", type: "number", placeholder: "مثال: 320" },
        { key: "author", labelAr: "المؤلف", labelEn: "Author", type: "text", placeholder: "مثال: أ. محمد أحمد" },
        { key: "pub_year", labelAr: "سنة النشر", labelEn: "Publishing Year", type: "number", placeholder: "مثال: 2024" }
      ];
    } else if (nameEn.includes("codes") || nameAr.includes("أكواد") || nameAr.includes("كود")) {
      return [
        { key: "validity_months", labelAr: "صلاحية التفعيل بالشهور", labelEn: "Validity (Months)", type: "number", placeholder: "مثال: 12" },
        { key: "max_users", labelAr: "الأجهزة المسموح بها", labelEn: "Allowed Devices", type: "number", placeholder: "مثال: 2" }
      ];
    } else if (nameEn.includes("courses") || nameAr.includes("دورات") || nameAr.includes("كورسات")) {
      return [
        { key: "lectures_count", labelAr: "عدد المحاضرات", labelEn: "Lectures Count", type: "number", placeholder: "مثال: 24" },
        { key: "duration_hours", labelAr: "مدة الكورس بالتقريب (ساعات)", labelEn: "Duration (Hours)", type: "number", placeholder: "مثال: 45" }
      ];
    }
    return [
      { key: "extra_info", labelAr: "معلومات إضافية", labelEn: "Additional Info", type: "text", placeholder: "أدخل أي تفاصيل إضافية..." }
    ];
  }, [selectedCategoryObj]);

  const handleCustomAttributeChange = (key, value) => {
    let cleanValue = value;
    const numericCustomKeys = [
      "page_count",
      "pub_year",
      "validity_months",
      "max_users",
      "lectures_count",
      "duration_hours"
    ];
    if (numericCustomKeys.includes(key)) {
      cleanValue = value.replace(/-/g, "");
      const numVal = parseFloat(cleanValue);
      if (numVal < 0) {
        return;
      }
    }

    setCustomAttributes((prev) => ({
      ...prev,
      [key]: cleanValue,
    }));
  };

  const generateCartesianCombinations = (activeVarIds, activeVarValues) => {
    const selectedLists = activeVarIds
      .map(vId => {
        const values = activeVarValues[vId] || [];
        return { variantId: vId, values };
      })
      .filter(item => item.values.length > 0);

    if (selectedLists.length === 0) return [];

    const cartesian = (lists) => {
      return lists.reduce((a, b) => {
        return a.flatMap(d => {
          return b.values.map(e => {
            const valObj = variantValues.find(val => val.id === e);
            const varObj = variants.find(v => v.id === b.variantId);
            return [
              ...d,
              {
                variant_id: b.variantId,
                variant_value_id: e,
                 value_name_ar: valObj?.name_ar || (typeof valObj?.name === 'object' && valObj?.name ? valObj.name.ar : valObj?.name) || "",
                 value_name_en: valObj?.name_en || (typeof valObj?.name === 'object' && valObj?.name ? valObj.name.en : valObj?.name) || "",
                 variant_name_ar: varObj?.name_ar || (typeof varObj?.name === 'object' && varObj?.name ? varObj.name.ar : varObj?.name) || "",
                 variant_name_en: varObj?.name_en || (typeof varObj?.name === 'object' && varObj?.name ? varObj.name.en : varObj?.name) || ""
              }
            ];
          });
        });
      }, [[]]);
    };

    const rawCombinations = cartesian(selectedLists);
    const generatedSkusMap = {};

    return rawCombinations.map((comb, index) => {
      const combSkuSuffix = comb.map(c => c.value_name_en.replace(/\s+/g, '_')).join("-");
      const baseSku = formData.sku ? `${formData.sku}-${combSkuSuffix}` : `sku-${index}-${combSkuSuffix}`;
      
      let finalSku = baseSku;
      let counter = 1;
      while (generatedSkusMap[finalSku]) {
        finalSku = `${baseSku}-${counter}`;
        counter++;
      }
      generatedSkusMap[finalSku] = true;

      return {
        items: comb,
        price: formData.real_price || "",
        quantity: formData.qty || "",
        sku: finalSku
      };
    });
  };

  const handleVariantValueCheckboxChange = (variantId, valueId, checked) => {
    setSelectedVariantValues(prev => {
      const currentValues = prev[variantId] || [];
      let newValues;
      if (checked) {
        newValues = [...currentValues, valueId];
      } else {
        newValues = currentValues.filter(id => id !== valueId);
      }
      const updated = { ...prev, [variantId]: newValues };
      
      const newCombs = generateCartesianCombinations(selectedVariants, updated);
      setGeneratedSKUs(newCombs);
      
      return updated;
    });
  };

  const handleVariantCheckboxChange = (variantId, checked) => {
    let newSelectedVariants;
    if (checked) {
      newSelectedVariants = [...selectedVariants, variantId];
    } else {
      newSelectedVariants = selectedVariants.filter(id => id !== variantId);
    }
    setSelectedVariants(newSelectedVariants);

    const updatedValues = { ...selectedVariantValues };
    if (!checked) {
      delete updatedValues[variantId];
    }
    setSelectedVariantValues(updatedValues);

    const newCombs = generateCartesianCombinations(newSelectedVariants, updatedValues);
    setGeneratedSKUs(newCombs);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let cleanValue = value;

    // منع الأرقام السالبة للحقول الرقمية
    if (
      ["fake_price", "real_price", "discount", "qty", "stock", "sort_by"].includes(name)
    ) {
      cleanValue = value.replace(/-/g, "");
      const numValue = parseFloat(cleanValue);
      if (numValue < 0) {
        return; // منع إدخال القيم السالبة
      }
    }

    if (name === "category_id") {
      const newCatObj = categories?.find(c => String(c.id) === String(cleanValue));
      const isEdu = isBookOrCodeCategory(newCatObj);
      if (!isEdu) {
        setFormData((prev) => ({
          ...prev,
          category_id: cleanValue,
          grade_id: "",
          subject_id: "",
          section_id: "",
          teacher_id: "",
        }));
        return;
      } else {
        setSelectedVariants([]);
        setSelectedVariantValues({});
        setGeneratedSKUs([]);
      }
    }

    // BUG-49 fix: حقل "المخزون" كان يُستبدل دائماً بقيمة "الكمية" عند الإرسال (qty: totalQty, stock: totalQty)
    // مما يجعل إدخال المستخدم في حقل المخزون يُتجاهل بصمت. الحل: مزامنة المخزون تلقائياً مع الكمية
    // بدلاً من طلب إدخالها يدوياً مرتين، وجعل حقل المخزون للعرض فقط (راجع الحقول أدناه في الفورم).
    setFormData((prev) => ({
      ...prev,
      [name]: cleanValue,
      ...(name === "qty" ? { stock: cleanValue } : {}),
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      features: newFeatures,
    }));
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, { text_en: "", text_ar: "" }],
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        features: newFeatures,
      }));
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      sliders: files,
    }));

    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const validateForm = () => {
    const newErrors = {};

    // التحقق من عدم وجود قيم سالبة للأسعار والكميات والمخزون والترتيب
    if (parseFloat(formData.fake_price) < 0) {
      newErrors.fake_price = "السعر القديم لا يمكن أن يكون سالباً";
    }
    if (parseFloat(formData.real_price) < 0) {
      newErrors.real_price = "السعر الحالي لا يمكن أن يكون سالباً";
    }
    if (parseFloat(formData.qty) < 0) {
      newErrors.qty = "الكمية لا يمكن أن تكون سالبة";
    }
    if (parseFloat(formData.stock) < 0) {
      newErrors.stock = "المخزون لا يمكن أن يكون سالباً";
    }
    if (parseFloat(formData.sort_by) < 0) {
      newErrors.sort_by = "الترتيب لا يمكن أن يكون سالباً";
    }

    // التحقق من عدم وجود قيم سالبة للمواصفات الإضافية الرقمية
    const numericCustomKeys = [
      "page_count",
      "pub_year",
      "validity_months",
      "max_users",
      "lectures_count",
      "duration_hours"
    ];
    for (const key of numericCustomKeys) {
      if (customAttributes[key] !== undefined && customAttributes[key] !== "") {
        const val = parseFloat(customAttributes[key]);
        if (!isNaN(val) && val < 0) {
          newErrors[key] = "هذا الحقل لا يمكن أن يحتوي على قيمة سالبة";
        }
      }
    }

    // العناوين
    if (!formData.title_ar.trim())
      newErrors.title_ar = "العنوان بالعربية مطلوب";
    if (!formData.title_en.trim())
      newErrors.title_en = "العنوان بالإنجليزية مطلوب";

    // الأسعار
    const isVariantsActive = showVariantsUI && generatedSKUs.length > 0;

    if (!isVariantsActive && (!formData.real_price || parseFloat(formData.real_price) <= 0)) {
      newErrors.real_price = "السعر الحالي مطلوب ويجب أن يكون أكبر من صفر";
    }
    if (!isVariantsActive && !formData.qty) {
      newErrors.qty = "الكمية مطلوبة";
    }
    if (!isVariantsActive && !formData.stock) {
      newErrors.stock = "المخزون مطلوب";
    }
    if (!isVariantsActive && !formData.sku) {
      newErrors.sku = "وحدة حفظ المخزون مطلوبة";
    }
    if (!formData.category_id) {
      newErrors.category_id = "حقل التصنيف مطلوب";
    }

    // BUG-51 fix: التحقق من أن نسبة الخصم بين 0 و100
    if (formData.discount !== "" && formData.discount !== null && formData.discount !== undefined) {
      const discountVal = parseFloat(formData.discount);
      if (isNaN(discountVal) || discountVal < 0 || discountVal > 100) {
        newErrors.discount = "نسبة الخصم يجب أن تكون بين 0 و100";
      }
    }

    // Validate SKU rows if active
    if (isVariantsActive) {
      let invalidRow = false;
      generatedSKUs.forEach((skuRow) => {
        if (!skuRow.sku || !skuRow.sku.trim() || !skuRow.price || parseFloat(skuRow.price) <= 0 || !skuRow.quantity) {
          invalidRow = true;
        }
      });
      if (invalidRow) {
        newErrors.variants = "يرجى ملء جميع حقول الـ SKU والسعر والكمية لكل التركيبات المضافة";
      }
    }

    // الخصم (لو فاضي يتعيّن بـ 0)
    if (formData.discount === "" || formData.discount == null) {
      formData.discount = "0";
    }

    // لو fake > real → احسب الخصم تلقائي فقط إذا لم يدخل المستخدم خصم
    const fakePrice = parseFloat(formData.fake_price);
    const realPrice = parseFloat(formData.real_price);
    const userDiscount = parseFloat(formData.discount);

    if (fakePrice > 0 && realPrice > 0) {
      if (fakePrice > realPrice && userDiscount === 0) {
        // احسب الخصم تلقائياً فقط إذا لم يدخل المستخدم خصم
        formData.discount = (
          ((fakePrice - realPrice) / fakePrice) *
          100
        ).toFixed(1);
      } else if (fakePrice <= realPrice) {
        formData.discount = "0";
      }
    }

    // المميزات
    const validFeatures = formData.features.filter(
      (f) => f.text_ar.trim() && f.text_en.trim()
    );
    // المميزات اختيارية — لا يتم فرض إضافة ميزة

    // التحقق من أن الكمية لا تكون أكبر من المخزون
    if (!isVariantsActive) {
      const qty = parseFloat(formData.qty);
      const stock = parseFloat(formData.stock);
      if (qty > 0 && stock > 0 && qty > stock) {
        newErrors.qty = "الكمية لا يمكن أن تكون أكبر من المخزون";
      }
    }

    // الصور
    if (formData.sliders.length === 0) {
      newErrors.sliders = "يجب إضافة صورة واحدة على الأقل";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((err) =>
        toast.error(err, {
          position: "top-center",
          zIndex: 9999,
        })
      );
      return;
    }

    const isVariantsActive = showVariantsUI && generatedSKUs.length > 0;
    
    // Dynamic sum of quantity for main fields if variants are active
    const totalQty = isVariantsActive
      ? generatedSKUs.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
      : formData.qty;

    const firstSkuPrice = isVariantsActive && generatedSKUs[0]
      ? generatedSKUs[0].price
      : formData.real_price;

    const firstSkuCode = isVariantsActive && generatedSKUs[0]
      ? generatedSKUs[0].sku
      : formData.sku;

    // تجهيز الداتا
    const cleanedData = {
      ...formData,
      qty: totalQty,
      stock: totalQty,
      real_price: firstSkuPrice,
      sku: firstSkuCode,
      is_sku: isVariantsActive ? 1 : 0,
      product_sku: isVariantsActive
        ? generatedSKUs.map(skuRow => ({
            sku: skuRow.sku,
            price: skuRow.price,
            quantity: skuRow.quantity,
            items: skuRow.items.map(it => ({
              variant_id: it.variant_id,
              variant_value_id: it.variant_value_id
            }))
          }))
        : [],
      is_preorder: formData.is_preorder === 1 ? 1 : 0,
      preorder_capacity: Number(formData.preorder_capacity) || 0,
      // Convert empty strings to null for foreign keys to allow optionality in backend
      category_id: formData.category_id === "" ? null : formData.category_id,
      grade_id: formData.grade_id === "" ? null : formData.grade_id,
      subject_id: formData.subject_id === "" ? null : formData.subject_id,
      section_id: formData.section_id === "" ? null : formData.section_id,
      features: formData.features.filter(
        (f) => f.text_ar.trim() && f.text_en.trim()
      ),
      custom_attributes: customAttributes,
    };

    dispatch(addProduct(cleanedData));
  };

  useEffect(() => {
 

  if (addSuccessMessage) {
    const successMessage =
      i18n.language === "ar"
        ? "تمت الإضافة بنجاح"
        : addSuccessMessage || "Added successfully";

    toast.success(successMessage, {
      position: "top-center",
      zIndex: 9999,
    });

    dispatch(clearState());
    if (onSuccess) onSuccess();
  }
   if (addError) {
    // Step 1: Extract the first error message from "errors" or fallback
    let errorMessage = "حدث خطأ غير متوقع";

    if (typeof addError === "string") {
      errorMessage = addError;
    } else if (addError.errors && typeof addError.errors === "object") {
      // Take first error message from first key
      const firstKey = Object.keys(addError.errors)[0];
      if (firstKey && Array.isArray(addError.errors[firstKey]) && addError.errors[firstKey].length > 0) {
        errorMessage = addError.errors[firstKey][0];
      }
    } else if (addError.message) {
      errorMessage = addError.message;
    }

    // Step 2: Normalize for translation (lowercase, remove punctuation)
    const normalized = errorMessage.toLowerCase().replace(/[.!]/g, "");

    // Step 3: Translate to Arabic if needed
    if (i18n.language === "ar") {
      if (normalized.includes("sku") && normalized.includes("has already been taken")) {
        errorMessage = "تم استخدام هذا الـ SKU من قبل";
      } else if (normalized.includes("field is required")) {
        errorMessage = "هذا الحقل مطلوب";
      } else if (normalized.includes("not found")) {
        errorMessage = "العنصر غير موجود";
      } else if (normalized.includes("unauthorized")) {
        errorMessage = "غير مصرح لك بتنفيذ هذا الإجراء";
      }
    }

    toast.error(errorMessage, {
      position: "top-center",
      zIndex: 9999,
    });

    dispatch(clearState());
  }
}, [addError, addSuccessMessage, dispatch, onSuccess, i18n.language]);

  return (
    <div className="card shadow p-4">

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* العنوان */}
          <div className="col-md-6 mb-3">
            <label className="form-label">العنوان (العربية)</label>
            <input
              type="text"
              name="title_ar"
              className={`form-control ${errors.title_ar ? "is-invalid" : ""}`}
              value={formData.title_ar}
              onChange={handleInputChange}
            />
            {errors.title_ar && (
              <div className="invalid-feedback">{errors.title_ar}</div>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">العنوان (الإنجليزية)</label>
            <input
              type="text"
              name="title_en"
              className={`form-control ${errors.title_en ? "is-invalid" : ""}`}
              value={formData.title_en}
              onChange={handleInputChange}
            />
            {errors.title_en && (
              <div className="invalid-feedback">{errors.title_en}</div>
            )}
          </div>

          {/* العنوان الفرعي */}
          <div className="col-md-6 mb-3">
            <label className="form-label">العنوان الفرعي (العربية)</label>
            <input
              type="text"
              name="subtitle_ar"
              className="form-control"
              value={formData.subtitle_ar}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">العنوان الفرعي (الإنجليزية)</label>
            <input
              type="text"
              name="subtitle_en"
              className="form-control"
              value={formData.subtitle_en}
              onChange={handleInputChange}
            />
          </div>

          {/* الوصف */}
          <div className="col-md-6 mb-3">
            <label className="form-label">الوصف (العربية)</label>
            <textarea
              name="desc_ar"
              className="form-control"
              rows="3"
              value={formData.desc_ar}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">الوصف (الإنجليزية)</label>
            <textarea
              name="desc_en"
              className="form-control"
              rows="3"
              value={formData.desc_en}
              onChange={handleInputChange}
            />
          </div>
          
          {/* الملاحظات */}
          <div className="col-md-6 mb-3">
            <label className="form-label">ملاحظات (العربية)</label>
            <textarea
              name="note_ar"
              className="form-control"
              rows="2"
              value={formData.note_ar}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">ملاحظات (الإنجليزية)</label>
            <textarea
              name="note_en"
              className="form-control"
              rows="2"
              value={formData.note_en}
              onChange={handleInputChange}
            />
          </div>


          {/* الأسعار */}
          <div className="col-md-4 mb-3">
            <label className="form-label">السعر القديم</label>
            <input
              type="number"
              name="fake_price"
              className={`form-control ${
                errors.fake_price ? "is-invalid" : ""
              }`}
              value={formData.fake_price}
              onChange={handleInputChange}
              min={0}
              step={0.01}
            />
            {errors.fake_price && (
              <div className="invalid-feedback">{errors.fake_price}</div>
            )}
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">السعر الحالي *</label>
            <input
              type="number"
              name="real_price"
              className={`form-control ${
                errors.real_price ? "is-invalid" : ""
              }`}
              value={formData.real_price}
              onChange={handleInputChange}
              min={0}
              step={0.01}
            />
            {errors.real_price && (
              <div className="invalid-feedback">{errors.real_price}</div>
            )}
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">الخصم (%) *</label>
            <input
              type="number"
              name="discount"
              className={`form-control ${errors.discount ? "is-invalid" : ""}`}
              value={formData.discount}
              onChange={handleInputChange}
              min={0}
              max={100}
              step={0.1}
            />
            {errors.discount && (
              <div className="invalid-feedback">{errors.discount}</div>
            )}
          </div>

          {/* المخزون */}
          <div className="col-md-6 mb-3">
            <label className="form-label">الكمية *</label>
            <input
              type="number"
              name="qty"
              className={`form-control ${errors.qty ? "is-invalid" : ""}`}
              value={formData.qty}
              onChange={handleInputChange}
              min={0}
            />
            {errors.qty && <div className="invalid-feedback">{errors.qty}</div>}
          </div>
          <div className="col-md-6 mb-3">
            {/* BUG-49 fix: هذا الحقل كان يبدو كإدخال مستقل رغم أن قيمته تُستبدل دائماً بقيمة "الكمية" عند الحفظ،
                مما يسبب لبس وإدخال بيانات مكرر. الآن يُحدَّث تلقائياً من حقل الكمية ويُعرض للتوضيح فقط. */}
            <label className="form-label">
              المخزون الإجمالي *{" "}
              <small className="text-muted fw-normal">(يُحدَّث تلقائياً بنفس قيمة الكمية)</small>
            </label>
            <input
              type="number"
              name="stock"
              className={`form-control ${errors.stock ? "is-invalid" : ""}`}
              value={formData.stock}
              readOnly
              disabled
              min={0}
            />
            {errors.stock && (
              <div className="invalid-feedback">{errors.stock}</div>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">وحدة حفظ المخزون *</label>
            <input
              type="text"
              name="sku"
              className={`form-control ${errors.sku ? "is-invalid" : ""}`}
              value={formData.sku}
              required
              onChange={handleInputChange}
            />
            {errors.sku && (
              <div className="invalid-feedback">{errors.sku}</div>
            )}
          </div>

          {/* الحالة */}
          <div className="col-md-6 mb-3">
            <label className="form-label">الحالة</label>
            <select
              name="status"
              className="form-control"
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value={1}>نشط</option>
              <option value={0}>غير نشط</option>
            </select>
          </div>

          {/* التصنيف */}
          <div className="col-md-6 mb-3">
            <label className="form-label">التصنيف (إجباري)</label>
            <select
              name="category_id"
              className={`form-control ${
                errors.category_id ? "is-invalid" : ""
              }`}
              value={formData.category_id}
              onChange={handleInputChange}
            >
              <option value="">اختر التصنيف</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name_ar}
                </option>
              ))}
            </select>
            {errors.category_id && (
              <div className="invalid-feedback">{errors.category_id}</div>
            )}
          </div>

          {/* Dynamic Category Attributes */}
          {dynamicAttributes && dynamicAttributes.length > 0 && (
            <div className="col-12 mb-3">
              <div className="card border-0 bg-light p-3 rounded shadow-sm" style={{ borderRight: "4px solid #dc3545" }}>
                <h6 className="font-weight-bold text-danger mb-3">
                  مواصفات إضافية للقسم المختار ({selectedCategoryObj?.name_ar || selectedCategoryObj?.name})
                </h6>
                <div className="row">
                  {dynamicAttributes.map((attr) => (
                    <div key={attr.key} className="col-md-4 mb-3 text-start">
                      <label className="form-label">{attr.labelAr}</label>
                      <input
                        type={attr.type}
                        min={attr.type === "number" ? 0 : undefined}
                        className="form-control"
                        placeholder={attr.placeholder}
                        value={customAttributes[attr.key] || ""}
                        onChange={(e) => handleCustomAttributeChange(attr.key, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isEducational && (
            <div className="col-12 mb-3">
              <div className="card border-0 bg-light p-3 rounded shadow-sm">
                <h6 className="font-weight-bold text-primary mb-3">البيانات التعليمية للمنتج</h6>
                <div className="row">
                  {/* الصف */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">الصف (اختياري)</label>
                    <select
                      name="grade_id"
                      className={`form-control ${errors.grade_id ? "is-invalid" : ""}`}
                      value={formData.grade_id}
                      onChange={handleInputChange}
                    >
                      <option value="">اختر الصف</option>
                      {grades?.map((grade) => (
                        <option key={grade.id} value={grade.id}>
                          {grade.name_ar}
                        </option>
                      ))}
                    </select>
                    {errors.grade_id && (
                      <div className="invalid-feedback">{errors.grade_id}</div>
                    )}
                  </div>

                  {/* المادة */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">المادة (اختياري)</label>
                    <select
                      name="subject_id"
                      className={`form-control ${
                        errors.subject_id ? "is-invalid" : ""
                      }`}
                      value={formData.subject_id}
                      onChange={handleInputChange}
                    >
                      <option value="">اختر المادة</option>
                      {subjects?.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name_ar}
                        </option>
                      ))}
                    </select>
                    {errors.subject_id && (
                      <div className="invalid-feedback">{errors.subject_id}</div>
                    )}
                  </div>

                  {/* الشعبة */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">الشعبة (اختياري)</label>
                    <select
                      name="section_id"
                      className={`form-control ${
                        errors.section_id ? "is-invalid" : ""
                      }`}
                      value={formData.section_id}
                      onChange={handleInputChange}
                    >
                      <option value="">اختر الشعبة</option>
                      {sections?.map((section) => (
                        <option key={section.id} value={section.id}>
                          {section.name_ar}
                        </option>
                      ))}
                    </select>
                    {errors.section_id && (
                      <div className="invalid-feedback">{errors.section_id}</div>
                    )}
                  </div>

                  {/* المعلم */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="teacher_id" className="form-label">المعلم (اختياري)</label>
                    <select
                      className="form-select"
                      id="teacher_id"
                      name="teacher_id"
                      value={formData.teacher_id}
                      onChange={handleInputChange}
                    >
                      <option value="">اختر المعلم</option>
                      {all_teachers?.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name_ar}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* الترتيب */}
          <div className="col-md-6 mb-3">
            <label className="form-label">الترتيب</label>
            <input
              type="number"
              name="sort_by"
              className="form-control"
              value={formData.sort_by}
              onChange={handleInputChange}
              min={0}
            />
          </div>

          {/* المميزات */}
          <div className="col-12 mb-3">
            <label className="form-label">المميزات</label>
            {formData.features.map((feature, index) => (
              <div key={index} className="row mb-2">
                <div className="col-md-5">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="الميزة (العربية)"
                    value={feature.text_ar}
                    onChange={(e) =>
                      handleFeatureChange(index, "text_ar", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-5">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="الميزة (الإنجليزية)"
                    value={feature.text_en}
                    onChange={(e) =>
                      handleFeatureChange(index, "text_en", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => removeFeature(index)}
                    disabled={formData.features.length === 1}
                    title="حذف الميزة"
                  >
                    <i className="fas fa-trash me-1"></i>
                    حذف
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={addFeature}
            >
              <i className="fas fa-plus me-1"></i>
              إضافة ميزة
            </button>
            {errors.features && (
              <div className="text-danger mt-2">{errors.features}</div>
            )}
          </div>
          <div className="col-md-12 mb-3">
            <label class="form-check-label" htmlFor="flexSwitchCheckDefault">
              تفعيل الطلب المسبق
            </label>
            <div class="form-check form-switch d-flex my-2">
              <input
                className="form-check-input"
                type="checkbox"
                name="is_preorder"
                role="switch"
                checked={formData.is_preorder === "1"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    is_preorder: e.target.checked ? "1" : "0",
                  })
                }
                id="flexSwitchCheckDefault"
              />
            </div>
          </div>
          <div className="col-md-12 mb-3">
            <label className="form-label">سعة الطلب المسبق</label>
            <input
              type="number"
              min="0"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, ""); // only digits
              }}
              name="preorder_capacity"
              className={`form-control`}
              value={formData.preorder_capacity}
              onChange={handleInputChange}
            />
            {/* {errors.qty && <div className="invalid-feedback">{errors.qty}</div>} */}
          </div>

          <div className="col-md-12 mb-3">
            <label className="form-check-label" htmlFor="isFreeShipping">
              شحن مجاني
            </label>
            <div className="form-check form-switch d-flex my-2">
              <input
                className="form-check-input"
                type="checkbox"
                name="is_free_shipping"
                role="switch"
                checked={formData.is_free_shipping === 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    is_free_shipping: e.target.checked ? 1 : 0,
                  })
                }
                id="isFreeShipping"
              />
            </div>
          </div>
          {showVariantsUI && (
            <div className="col-12 mt-4 mb-3 border-top pt-4">
              <h5 className="font-weight-bold text-primary mb-3">خصائص ومواصفات المنتج (Variants)</h5>
              
              {/* List Variants */}
              <div className="card bg-light p-3 mb-3">
                <h6 className="font-weight-bold mb-2">اختر الخصائص المتاحة:</h6>
                <div className="d-flex flex-wrap gap-3">
                  {variants?.map((v) => {
                    const isChecked = selectedVariants.includes(v.id);
                    return (
                      <div key={v.id} className="form-check form-check-inline border p-2 rounded bg-white shadow-sm">
                        <input
                          className="form-check-input ms-2"
                          type="checkbox"
                          id={`var-${v.id}`}
                          checked={isChecked}
                          onChange={(e) => handleVariantCheckboxChange(v.id, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`var-${v.id}`}>
                          {v.name_ar || (typeof v.name === 'object' && v.name ? v.name.ar || v.name.en : v.name) || v.key}
                        </label>
                      </div>
                    );
                  })}
                  {variants?.length === 0 && (
                    <span className="text-muted text-center w-100">لا توجد خصائص مضافة حالياً.</span>
                  )}
                </div>
              </div>

              {/* List Values for Selected Variants */}
              {selectedVariants.length > 0 && (
                <div className="card p-3 mb-3">
                  <h6 className="font-weight-bold mb-2">اختر قيم الخصائص المحددة:</h6>
                  {selectedVariants.map((vId) => {
                    const variantObj = variants.find(v => v.id === vId);
                    const valuesForVariant = variantValues.filter(val => String(val.variant_id || val.variant?.id) === String(vId));
                    return (
                      <div key={vId} className="border-bottom pb-2 mb-2">
                        <strong className="text-secondary">{variantObj?.name_ar || (typeof variantObj?.name === 'object' && variantObj?.name ? variantObj.name.ar || variantObj.name.en : variantObj?.name) || variantObj?.key}:</strong>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {valuesForVariant.map((val) => {
                            const isChecked = (selectedVariantValues[vId] || []).includes(val.id);
                            return (
                              <div key={val.id} className="form-check form-check-inline">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`val-${val.id}`}
                                  checked={isChecked}
                                  onChange={(e) => handleVariantValueCheckboxChange(vId, val.id, e.target.checked)}
                                />
                                <label className="form-check-label ms-2" htmlFor={`val-${val.id}`}>
                                  {val.name_ar || (typeof val.name === 'object' && val.name ? val.name.ar || val.name.en : val.name) || ""}
                                </label>
                              </div>
                            );
                          })}
                          {valuesForVariant.length === 0 && (
                            <span className="text-muted small">لا توجد قيم لهذه الخاصية.</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Cartesian combinations Table */}
              {generatedSKUs.length > 0 && (
                <div className="card p-3">
                  <h6 className="font-weight-bold text-success mb-3">جدول التركيبات المنشأة (SKUs)</h6>
                  {errors.variants && (
                    <div className="alert alert-danger py-2">{errors.variants}</div>
                  )}
                  <div className="table-responsive">
                    <table className="table table-bordered text-center align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>التركيبة (الخصائص)</th>
                          <th>رمز المخزون (SKU) *</th>
                          <th>السعر (ج.م) *</th>
                          <th>الكمية *</th>
                          <th>الإجراء</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generatedSKUs.map((skuRow, idx) => {
                          const combText = skuRow.items.map(it => `${it.variant_name_ar}: ${it.value_name_ar}`).join(" | ");
                          return (
                            <tr key={idx}>
                              <td><strong>{combText}</strong></td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm text-center"
                                  value={skuRow.sku}
                                  required
                                  onChange={(e) => {
                                    const updated = [...generatedSKUs];
                                    updated[idx].sku = e.target.value;
                                    setGeneratedSKUs(updated);
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="form-control form-control-sm text-center"
                                  value={skuRow.price}
                                  required
                                  onChange={(e) => {
                                    const updated = [...generatedSKUs];
                                    updated[idx].price = e.target.value;
                                    setGeneratedSKUs(updated);
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  className="form-control form-control-sm text-center"
                                  value={skuRow.quantity}
                                  required
                                  onChange={(e) => {
                                    const updated = [...generatedSKUs];
                                    updated[idx].quantity = e.target.value;
                                    setGeneratedSKUs(updated);
                                  }}
                                />
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => {
                                    const updated = generatedSKUs.filter((_, i) => i !== idx);
                                    setGeneratedSKUs(updated);
                                  }}
                                >
                                  إزالة
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* الصور */}
          <div className="col-12 mb-3">
            <label className="form-label">الصور</label>
            <input
              type="file"
              multiple
              className={`form-control ${errors.sliders ? "is-invalid" : ""}`}
              onChange={handleImageChange}
              accept="image/*"
            />
            {errors.sliders && (
              <div className="invalid-feedback">{errors.sliders}</div>
            )}
            <div className="d-flex flex-wrap mt-3">
              {imagePreviews.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt="preview"
                  className="me-2 mb-2"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button
            type="submit"
            className="btn btn-danger"
            disabled={addLoading}
          >
            {addLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                جاري الحفظ...
              </>
            ) : (
              "اضافة منتج"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProduct;
